import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  Product,
  ProductLabel,
  ProductRepository,
  ProductStockNotificationRepository,
  ProductVariantReport,
  Variant,
  VariantRepository,
  Where
} from '@infrab4a/connect';
import { lastValueFrom } from 'rxjs';
import {
  TryOnCalibration,
  TryOnCategory,
  TryOnFinish,
  TryOnMode,
  TryOnProductSetInput,
  TryOnRowSetInput,
  TryOnShade
} from 'src/app/components/shop-product-form/shop-product-try-on-form/shop-product-try-on.util';
import { PageContent } from 'src/app/components/table';
import { PageableFilter } from 'src/app/admin-api';
import { ConnectRequest } from 'src/app/models';
import { StorageService } from 'src/app/services/auth/storage.service';
import { Supplier } from 'src/app/services/supplier.service';
import { environment } from 'src/environments/environment';
import { BaseConnectService } from './base-connect.service';

export interface ProductCuradoriaFields {
  caixariaMinima: number | null;
  suppliers: Supplier[];
  curve: string | null;
}

export interface ProductTryOnRow {
  id: number;
  name: string | null;
  ean: string | null;
  sku: string | null;
  grade: Array<{ title: string; value: string }> | null;
  /**
   * Variant row: the tone is available for try-on. Parent row: the try-on is
   * shown on the PDP. Single-SKU products carry both meanings on one row.
   */
  try_on_enabled: boolean;
  variant_try_on_color: string | null;
  variant_try_on_finish: TryOnFinish | null;
  variant_try_on_intensity: number | null;
  variant_try_on_calibration: TryOnCalibration | null;
  updated_at: string | null;
}

export interface ProductTryOnFields extends ProductTryOnRow {
  try_on_category: TryOnCategory | null;
  try_on_mode: TryOnMode | null;
  try_on_disclaimer: string | null;
  /**
   * Palette tones of a single-SKU parent (JSONB, raw as returned by Hasura;
   * parse with `parseTryOnShades`). Null/[] = legacy `variant_try_on_*`.
   */
  try_on_shades: TryOnShade[] | null;
  variants: ProductTryOnRow[];
}

export interface ProductExpiration {
  internalId: number | null;
  expirationDate: string | null;
  stock: number | null;
  /** Balance of the specific lot shown with expirationDate (not the total). */
  lotStock: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class ShopProductService extends BaseConnectService<Product> {
  constructor(
    @Inject('ProductRepository')
    private productRepository: ProductRepository,
    @Inject('VariantRepository')
    private variantRepository: VariantRepository,
    @Inject('ProductStockNotificationRepository')
    private productStockNotificationRepository: ProductStockNotificationRepository,
    private http: HttpClient
  ) {
    super(productRepository);
  }

  async getProductById(id: string): Promise<Product> {
    return this.productRepository.get({ id });
  }

  async getVariantById(id: string): Promise<Variant> {
    return this.variantRepository.get({ id, productId: null });
  }

  async getProductBySKU(sku: string): Promise<Product> {
    return await this.productRepository
      .find({
        filters: {
          sku: {
            operator: Where.EQUALS,
            value: sku
          }
        }
      })
      .then((res) => res.data.shift());
  }

  async getProductByEan(ean: string): Promise<Product> {
    return await this.productRepository
      .find({
        filters: {
          EAN: {
            operator: Where.EQUALS,
            value: ean
          }
        }
      })
      .then((res) => res.data.shift());
  }

  async getProductOrVariantByEan(
    ean: string
  ): Promise<Product & { isVariant: boolean }> {
    let product = await this.productRepository
      .find({
        filters: {
          EAN: {
            operator: Where.EQUALS,
            value: ean
          }
        }
      })
      .then((res) => res.data.shift());

    const variant = await this.variantRepository
      .find({
        filters: {
          EAN: {
            operator: Where.EQUALS,
            value: ean
          }
        }
      })
      .then((res) => res.data.shift());

    if (!product && !variant) return null;

    if (variant) {
      product = await this.productRepository
        .find({
          fields: ['id', 'EAN', 'name'],
          filters: {
            id: {
              operator: Where.EQUALS,
              value: variant.productId
            }
          }
        })
        .then((res) => res.data.shift());
    }

    return {
      ...(product && { ...product.toPlain() }),
      isVariant: false,
      ...(variant && { ...variant.toPlain(), isVariant: true }),
      name: variant ? `${product.name} ${variant.grade[0].value}` : product.name
    };
  }

  async getProductsList(
    request: Partial<ConnectRequest>
  ): Promise<PageContent<Partial<Product>>> {
    const fields = [
      'id',
      'name',
      'EAN',
      'sku',
      'type',
      'hasVariants',
      'stock',
      'published',
      'label',
      'categoryId',
      'group',
      'validity',
      'createdAt',
      'updatedAt'
    ];
    const filters = await this.resolveTryOnEnabledFilter(request.filters);
    if (filters === null) {
      return { totalPages: 0, totalElements: 0, content: [], empty: true };
    }
    const result = await this.paginatedSearch({
      ...request,
      filters,
      fields
    });
    const tryOnEnabledByProductId = await this.getTryOnEnabledByIds(
      result.data.map((product) => product.id)
    );
    const content = result.data.map((product) => ({
      ...product,
      tryOnEnabled: tryOnEnabledByProductId.get(String(product.id)) ?? false
    }));
    const totalPages = request.pageSize
      ? Math.floor(result.count / request.pageSize)
      : undefined;
    const totalElements = result.count;

    return {
      totalPages,
      totalElements,
      content,
      empty: result.count === 0
    };
  }

  /**
   * `tryOnEnabled` is not a @infrab4a/connect Product field, so the listing
   * filter is translated into an `id in (...)` filter resolved via Hasura.
   * Returns null when the filter is active and no product matches.
   */
  private async resolveTryOnEnabledFilter(
    filters: PageableFilter[] | undefined
  ): Promise<PageableFilter[] | undefined | null> {
    const tryOnFilter = filters?.find((f) => f.field === 'tryOnEnabled');
    const rest = filters?.filter((f) => f.field !== 'tryOnEnabled');
    if (!tryOnFilter || tryOnFilter.value !== true) return rest;

    const ids = await this.findTryOnEnabledProductIds();
    if (!ids.length) return null;

    return [
      ...(rest ?? []),
      { field: 'id', condition: 'in', value: ids, fieldType: 'number' }
    ];
  }

  async findTryOnEnabledProductIds(): Promise<number[]> {
    const query = `query TryOnEnabledProducts {
      product(where: { try_on_enabled: { _eq: true }, main_product_id: { _is_null: true } }) { id }
    }`;
    const data = await this.hasuraRequest<{ product: Array<{ id: number }> }>(
      query,
      {}
    );
    return data?.product?.map((p) => p.id) ?? [];
  }

  async getTryOnEnabledByIds(
    ids: Array<string | number>
  ): Promise<Map<string, boolean>> {
    const pks = ids
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    if (!pks.length) return new Map();

    const query = `query TryOnEnabledByIds($ids: [Int!]!) {
      product(where: { id: { _in: $ids } }) { id try_on_enabled }
    }`;
    const data = await this.hasuraRequest<{
      product: Array<{ id: number; try_on_enabled: boolean }>;
    }>(query, { ids: pks });

    return new Map(
      (data?.product ?? []).map((p) => [String(p.id), !!p.try_on_enabled])
    );
  }

  async updateProduct(product: Partial<Product>): Promise<Product> {
    if (product.description)
      Object.keys(product.description).forEach((f) => {
        if (!product.description[f]?.length) {
          if (f === 'description')
            product.description[f] = BaseConnectService.NULL;
          else if (f === 'brand') {
            product.description[f] = null;
            product['brandDescription'] = BaseConnectService.NULL;
          } else {
            product.description[f] = null;
            product[f] = BaseConnectService.NULL;
          }
        }
      });
    product.label =
      (product.label as string) === 'null'
        ? BaseConnectService.NULL
        : product.label;

    return this.productRepository.update(Product.toInstance(product));
  }

  async filterByBrand(brandName: string) {
    return await this.productRepository.find({
      filters: {
        brand: { operator: Where.EQUALS, value: brandName }
      }
    });
  }

  async findProductVariants(productId: string): Promise<Variant[]> {
    const result = await this.variantRepository.find({
      filters: {
        productId: {
          operator: Where.EQUALS,
          value: productId
        }
      },
      limits: {
        limit: 9999,
        offset: 0
      }
    });
    return result.data;
  }

  async updateVariant(
    variant: Partial<Variant>,
    variantId: string
  ): Promise<Partial<Variant>> {
    return await this.variantRepository.update({ ...variant, id: variantId });
  }

  async getProductStockNotificationReport(request: Partial<ConnectRequest>) {
    console.log(request);
    const query = this.getFiltersQuery(request.filters) as any;

    const notifications =
      await this.productStockNotificationRepository.getNotificationsReport(
        {
          ean: query?.ean?.value,
          name: query?.name?.value,
          sku: query?.sku?.value,
          reference: query?.reference?.value,
          category: query?.category?.value,
          emailsCount: query?.emails_registered?.value
        },
        {
          field: request.sortBy,
          direction: request.sortDirection
        },
        {
          limit: request.pageSize,
          offset: request.pageSize * request.page
        }
      );

    const totalPages = request.pageSize
      ? Math.floor(notifications.count / request.pageSize)
      : undefined;

    return {
      totalPages,
      totalElements: notifications.count,
      content: notifications.data,
      empty: notifications.count === 0
    };
  }

  override get scheme(): 'firebase' | 'hasura' {
    return 'hasura';
  }

  public static getProductLabel(label?: ProductLabel): string {
    const labels = {
      glamstar: 'Glamstar',
      outlet: 'Outlet',
      'last-units': 'Últimas unidades',
      'on-sale': 'Promoção'
    };

    if (!label) return 'Sem label';

    return labels[label];
  }

  async productVariantReport(): Promise<ProductVariantReport[]> {
    return this.productRepository.productVariantFullReport().then();
  }

  removeImage(productId: string, image: string) {
    return this.http.post(
      `https://southamerica-east1-${environment.connectFirebase.projectId}.cloudfunctions.net/productRemoveSingleImage`,
      {
        productId,
        image: image.split('/').at(-1)
      }
    );
  }

  syncMillenniumProductsBySkus(skus: string[]) {
    return this.http.post(
      `https://southamerica-east1-${environment.connectFirebase.projectId}.cloudfunctions.net/productsSyncMilleniumProductsCallBySku`,
      {
        data: {
          skus
        }
      }
    );
  }

  /**
   * Reads the curadoria columns (caixaria_minima, suppliers, curve) directly
   * from Hasura. These columns are not part of the @infrab4a/connect Product
   * model, so they are queried/updated via raw GraphQL.
   */
  async getCuradoriaFields(
    id: string | number
  ): Promise<ProductCuradoriaFields> {
    const query = `query ProductCuradoria($id: Int!) {
      product_by_pk(id: $id) { caixaria_minima suppliers curve }
    }`;
    const data = await this.hasuraRequest<{
      product_by_pk: {
        caixaria_minima: number | null;
        suppliers: Supplier[] | null;
        curve: string | null;
      } | null;
    }>(query, { id: this.toProductPk(id) });

    const row = data?.product_by_pk;

    return {
      caixariaMinima: row?.caixaria_minima ?? null,
      suppliers: Array.isArray(row?.suppliers) ? row.suppliers : [],
      curve: row?.curve ?? null
    };
  }

  async updateCuradoriaFields(
    id: string | number,
    fields: Partial<ProductCuradoriaFields>
  ): Promise<void> {
    const set: Record<string, unknown> = {};
    if (fields.caixariaMinima !== undefined)
      set['caixaria_minima'] = fields.caixariaMinima;
    if (fields.suppliers !== undefined) set['suppliers'] = fields.suppliers;
    if (fields.curve !== undefined) set['curve'] = fields.curve;

    if (!Object.keys(set).length) return;

    const mutation = `mutation UpdateProductCuradoria($id: Int!, $set: product_set_input!) {
      update_product_by_pk(pk_columns: { id: $id }, _set: $set) { id }
    }`;
    await this.hasuraRequest(mutation, { id: this.toProductPk(id), set });
  }

  /**
   * Reads the MaIA Try-On columns of a product and of its variant rows
   * (variants are rows of `product` linked by main_product_id). These columns
   * are not part of the @infrab4a/connect Product model, hence raw GraphQL.
   */
  async getTryOnFields(id: string | number): Promise<ProductTryOnFields> {
    const rowFields = `id name ean sku grade try_on_enabled
      variant_try_on_color variant_try_on_finish variant_try_on_intensity
      variant_try_on_calibration updated_at`;
    const query = `query ProductTryOn($id: Int!) {
      product_by_pk(id: $id) {
        ${rowFields}
        try_on_category
        try_on_mode
        try_on_disclaimer
        try_on_shades
        variants(order_by: { id: asc }) { ${rowFields} }
      }
    }`;
    const data = await this.hasuraRequest<{
      product_by_pk: ProductTryOnFields | null;
    }>(query, { id: this.toProductPk(id) });

    if (!data?.product_by_pk) {
      throw new Error(`Produto ${id} não encontrado no catálogo.`);
    }

    return {
      ...data.product_by_pk,
      try_on_shades: data.product_by_pk.try_on_shades ?? null,
      variants: data.product_by_pk.variants ?? []
    };
  }

  async updateProductTryOnFields(
    id: string | number,
    set: TryOnProductSetInput
  ): Promise<void> {
    const mutation = `mutation UpdateProductTryOn($id: Int!, $set: product_set_input!) {
      update_product_by_pk(pk_columns: { id: $id }, _set: $set) { id }
    }`;
    await this.hasuraRequest(mutation, {
      id: this.toProductPk(id),
      set: { ...set, updated_at: new Date().toISOString() }
    });
  }

  async updateTryOnRowFields(
    id: string | number,
    set: TryOnRowSetInput
  ): Promise<void> {
    const mutation = `mutation UpdateProductTryOnRow($id: Int!, $set: product_set_input!) {
      update_product_by_pk(pk_columns: { id: $id }, _set: $set) { id }
    }`;
    await this.hasuraRequest(mutation, {
      id: this.toProductPk(id),
      set: { ...set, updated_at: new Date().toISOString() }
    });
  }

  /**
   * Resolves a shop product's Millenium internal id (produto) from the Hasura
   * product_millenium table. This is the id the admin-api uses to query the
   * Millenium stock balance (saldodeestoque?produto=...). Returns null when the
   * product has no Millenium mapping.
   */
  async resolveMilleniumInternalId(params: {
    productId?: string | number;
    sku?: string;
  }): Promise<number | null> {
    if (params.productId != null) {
      const query = `query MilleniumInternalIdByProduct($productId: Int!) {
        product_millenium(where: { product_id: { _eq: $productId } }, limit: 1) {
          internal_id
        }
      }`;
      const data = await this.hasuraRequest<{
        product_millenium: Array<{ internal_id: number | null }>;
      }>(query, { productId: this.toProductPk(params.productId) });
      const internalId = data?.product_millenium?.[0]?.internal_id;
      if (internalId != null) return internalId;
    }

    if (params.sku) {
      const query = `query MilleniumInternalIdBySku($sku: String!) {
        product_millenium(where: { cod_product: { _eq: $sku } }, limit: 1) {
          internal_id
        }
      }`;
      const data = await this.hasuraRequest<{
        product_millenium: Array<{ internal_id: number | null }>;
      }>(query, { sku: params.sku });
      const internalId = data?.product_millenium?.[0]?.internal_id;
      if (internalId != null) return internalId;
    }

    return null;
  }

  /**
   * Live expiration date (validade) from Millenium. The shop product is mapped
   * to its Millenium internal id via Hasura (product_millenium) and the admin-api
   * queries Millenium server-side (keeping the ERP token off the front). Returns
   * the earliest data_validade across lots (the lot itself is ignored).
   */
  async getMilleniumExpiration(params: {
    productId?: string | number;
    sku?: string;
    internalId?: number;
  }): Promise<ProductExpiration> {
    const internalId =
      params.internalId ?? (await this.resolveMilleniumInternalId(params));

    if (internalId == null) {
      return { internalId: null, expirationDate: null, stock: null, lotStock: null };
    }

    const res = await lastValueFrom(
      this.http.get<{ result: ProductExpiration }>(
        `${environment.apiUrl}/product/expiration`,
        {
          params: { internalId: String(internalId) },
          headers: { Authorization: `Bearer ${StorageService.token}` }
        }
      )
    );

    return (
      res?.result ?? {
        internalId,
        expirationDate: null,
        stock: null,
        lotStock: null
      }
    );
  }

  private toProductPk(id: string | number): number {
    const pk = Number(id);
    if (!Number.isFinite(pk)) {
      throw new Error(`Id de produto inválido: ${id}`);
    }
    return pk;
  }

  private async hasuraRequest<T>(
    query: string,
    variables: Record<string, unknown>
  ): Promise<T> {
    const res = await lastValueFrom(
      this.http.post<{ data: T; errors?: Array<{ message: string }> }>(
        environment.hasura.endpoint,
        { query, variables },
        {
          headers: {
            'content-type': 'application/json',
            'x-hasura-admin-secret': environment.hasura.adminSecret
          }
        }
      )
    );

    if (res?.errors?.length) {
      throw new Error(res.errors.map((e) => e.message).join('; '));
    }

    return res.data;
  }
}
